'use client';
import { useResource } from '@/lib/use-resource';
import type { ZoneEvent } from '@/lib/research/zone-reason';
import { formatZoneReason } from '@/lib/research/zone-reason';

interface ZoneChangesResp {
  asOf: string;
  alphaZone: {
    current: { symbol: string; name: string; enteredOn: string }[];
    recentEntries: ZoneEvent[];
    recentExits: ZoneEvent[];
  };
}

function relativeDate(dateStr: string, asOf: string): string {
  const diff = Math.round((Date.parse(asOf) - Date.parse(dateStr)) / 86_400_000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff <= 6) return `${diff}d ago`;
  return dateStr;
}

function pillarLabel(key: string): string {
  const labels: Record<string, string> = {
    profitability: 'Profitability',
    growth: 'Growth',
    safety: 'Safety',
    shareholderReturns: 'Shr. Returns',
  };
  return labels[key] ?? key;
}

function fmt(n: number | undefined): string {
  if (n == null || !Number.isFinite(n)) return '';
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}`;
}

function PillarBreakdown({ delta }: { delta: ZoneEvent['pillarDelta'] }) {
  if (!delta) return null;
  const entries = (Object.entries(delta) as [string, number | undefined][])
    .filter(([, v]) => v != null && Math.abs(v) >= 0.05)
    .sort((a, b) => Math.abs(b[1]!) - Math.abs(a[1]!))
    .slice(0, 3);
  if (entries.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
      {entries.map(([k, v]) => (
        <span key={k} className="text-[10px] text-[#64748b]">
          {pillarLabel(k)}{' '}
          <span className={v! >= 0 ? 'text-emerald-400' : 'text-red-400'}>{fmt(v)}</span>
        </span>
      ))}
    </div>
  );
}

function EventRow({ e, asOf, type }: { e: ZoneEvent; asOf: string; type: 'entry' | 'exit' }) {
  const isEntry = type === 'entry';
  const label = relativeDate(e.date, asOf);
  const reason = formatZoneReason(e);

  return (
    <div className="flex items-start gap-2.5 py-2 border-b border-[#1e293b] last:border-0">
      <div className="flex flex-col items-center gap-0.5 pt-0.5">
        <span className={`text-[10px] font-bold ${isEntry ? 'text-emerald-400' : 'text-red-400'}`}>
          {isEntry ? '▲' : '▼'}
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className={`font-mono font-bold text-[12px] ${isEntry ? 'text-emerald-200' : 'text-red-300'}`}>
            {e.symbol}
          </span>
          <span className="text-[11px] text-[#94a3b8] truncate">{e.name}</span>
          <span className="text-[10px] text-[#475569] ml-auto shrink-0">{label}</span>
        </div>
        <div className="text-[11px] text-[#64748b] mt-0.5">{reason}</div>
        {(e.reason === 'composite_improved' || e.reason === 'composite_fell' || e.reason === 'q_gate_cleared' || e.reason === 'q_gate_failed') && (
          <PillarBreakdown delta={e.pillarDelta} />
        )}
        {e.daysInZone != null && (
          <div className="text-[10px] text-[#475569] mt-0.5">{e.daysInZone}d in zone</div>
        )}
      </div>
    </div>
  );
}

export function ZonePulse() {
  const { data, error } = useResource<ZoneChangesResp>('/api/zone-changes');

  if (error) return <div className="text-[11px] text-red-400">Failed to load zone changes.</div>;
  if (!data) return <div className="text-[11px] text-[#64748b]">Loading zone pulse…</div>;

  const { asOf, alphaZone } = data;
  const recent = 14; // show last 14 days of activity
  const cutoff = new Date(asOf);
  cutoff.setDate(cutoff.getDate() - recent);
  const cutoffStr = cutoff.toISOString().slice(0, 10);

  const entries = alphaZone.recentEntries.filter(e => e.date >= cutoffStr && e.reason !== 'baseline');
  const exits   = alphaZone.recentExits.filter(e => e.date >= cutoffStr && e.reason !== 'baseline');

  // Merge + sort chronologically (newest first)
  const all = [
    ...entries.map(e => ({ e, type: 'entry' as const })),
    ...exits.map(e => ({ e, type: 'exit' as const })),
  ].sort((a, b) => b.e.date.localeCompare(a.e.date) || (a.type === 'entry' ? -1 : 1));

  return (
    <section className="bg-[#0d1117] border border-[#1e293b] rounded-lg overflow-hidden">
      <div className="px-3 py-2 border-b border-[#1e293b] flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="text-[12px] uppercase tracking-wider text-amber-300 font-semibold">Zone Pulse</div>
          <div className="text-[10px] text-[#64748b] mt-0.5">
            Daily composite-driven entries & exits · last {recent} days · as of {asOf}
          </div>
        </div>
        <div className="flex gap-3 text-[10px]">
          <span className="text-emerald-400">{entries.length} entered</span>
          <span className="text-red-400">{exits.length} exited</span>
        </div>
      </div>

      <div className="px-3 py-1">
        {all.length === 0 ? (
          <div className="text-[11px] text-[#64748b] italic py-3">No roster changes in the last {recent} days.</div>
        ) : (
          all.map(({ e, type }, i) => (
            <EventRow key={`${type}-${e.symbol}-${e.date}-${i}`} e={e} asOf={asOf} type={type} />
          ))
        )}
      </div>
    </section>
  );
}
